/**
 * CompareSymbolPanel — modal panel to add/remove a comparison overlay symbol.
 *
 * Rendered inside `ChartPanel` when the user clicks the "Compare" toolbar
 * button.  The compare entries (series refs, fetched data) are OWNED by
 * `ChartPanel` so they persist across open/close of this panel.  This
 * component is purely presentational + search.
 */
import React, { useState, useMemo } from "react";
import { ChartSymbol } from "../../types/chart.types";
import { OneMinuteBar } from "../../types/chart.types";
import "./CompareSymbolPanel.scss";

export interface CompareEntry {
  symbol: ChartSymbol;
  color: string;
  series: any; // ISeriesApi<"Line">
  bars: OneMinuteBar[];
  loading: boolean;
}

interface CompareSymbolPanelProps {
  entries: CompareEntry[];
  panelSymbol: ChartSymbol | null;
  availableSymbols: ChartSymbol[];
  colorTheme: string;
  onAdd: (sym: ChartSymbol) => void;
  onRemove: (symbol: string) => void;
  onClose: () => void;
}

const CompareSymbolPanel: React.FC<CompareSymbolPanelProps> = ({
  entries,
  panelSymbol,
  availableSymbols,
  colorTheme,
  onAdd,
  onRemove,
  onClose,
}) => {
  const [search, setSearch] = useState("");

  const filteredSymbols = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    return availableSymbols
      .filter(
        (s) =>
          s.symbol !== panelSymbol?.symbol &&
          !entries.some((e) => e.symbol.symbol === s.symbol) &&
          (s.symbol.toLowerCase().includes(q) ||
            (s.segment || "").toLowerCase().includes(q))
      )
      .slice(0, 20);
  }, [search, availableSymbols, panelSymbol, entries]);

  return (
    <div className={`compare-panel ${colorTheme}`}>
      <div className="compare-panel-header">
        <span className="compare-title">Compare Symbol</span>
        <button className="compare-close-btn" onClick={onClose} title="Close">
          ✕
        </button>
      </div>

      {/* Active comparisons */}
      {entries.length > 0 && (
        <div className="compare-entries">
          {entries.map((entry) => (
            <div key={entry.symbol.symbol} className="compare-entry">
              <span
                className="compare-color-dot"
                style={{ background: entry.color }}
              />
              <span className="compare-entry-symbol">{entry.symbol.symbol}</span>
              {entry.loading && (
                <span className="compare-entry-loading">Loading…</span>
              )}
              <button
                className="compare-entry-remove"
                onClick={() => onRemove(entry.symbol.symbol)}
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search to add */}
      <div className="compare-search-wrapper">
        <input
          className="compare-search-input"
          type="text"
          placeholder="Search symbol to compare..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      {filteredSymbols.length > 0 && (
        <div className="compare-results">
          {filteredSymbols.map((sym) => (
            <button
              key={sym.symbol}
              className="compare-result-item"
              onClick={() => {
                onAdd(sym);
                setSearch("");
              }}
            >
              <span className="compare-result-symbol">{sym.symbol}</span>
              {sym.segment && (
                <span className="compare-result-segment">{sym.segment}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {search.trim() && filteredSymbols.length === 0 && (
        <div className="compare-no-results">No matching symbols</div>
      )}
    </div>
  );
};

export default CompareSymbolPanel;
